import { createContext, useCallback } from 'react';

/**
 * Component-of-origin prefix on a derived CTA identifier.
 *
 * Derived identifiers are only as useful as they are attributable, and an href slug
 * alone collides across components — `arw_pricing` and `ftr_pricing` are different
 * placements of the same destination and want to be told apart in a report.
 */
export const CTA_PREFIXES = {
  /** MegaMenu and primary navigation. */
  nav: 'nav',
  /** Page footer, top bar and bottom bar. */
  footer: 'ftr',
  /** CtaButton, including Hero and PreFooter which render through it. */
  cta: 'cta',
  /** ArrowLink, used by CardGrid, FeaturedCards and NsiCard. */
  arrowLink: 'arw',
  /** LinkCollection cards. */
  linkCollection: 'lc',
  /** Inline links inside authored RichText markdown. */
  richText: 'rt',
} as const;

/** A CTA identifier prefix. */
export type CtaPrefix = (typeof CTA_PREFIXES)[keyof typeof CTA_PREFIXES];

/**
 * Prefix a `CtaButton` derives its identifier under, defaulting to `cta`.
 *
 * A placement decides the prefix, not the button, so it is set by the surrounding component
 * rather than through a CtaButton prop: CtaButton's props interface is its CMS authoring
 * schema, and a prop there would surface as an authorable field on every CtaButton block.
 * MegaMenu provides `nav` around its header CTAs so the bar and drawer renderings of one
 * CTA derive the same identifier.
 */
export const CtaTrackingPrefixContext = createContext<CtaPrefix>(CTA_PREFIXES.cta);

/**
 * Adobe Analytics traffic property carrying the CTA identifier.
 *
 * `prop28` is the variable SellDot's existing CTA reporting reads.
 * `LegoWidgetsConfig-AMSG2018/src/scripts/analytics/adobe-analytics.es` L72-73 sets it as
 * `s.linkTrackVars = "prop28"` then `s_objectID = s.prop28 = linkName`.
 */
const CTA_PROPERTY = 'prop28';

/** Adobe `linkType` for a link leading off site. */
const LINK_TYPE_EXTERNAL = 'e';

/** Adobe `linkType` for a custom on-site link. */
const LINK_TYPE_CUSTOM = 'o';

/** Element PolyO renders carrying the Adobe page name. */
const OMNITURE_ELEMENT_ID = 'omniture-details';
const OMNITURE_PAGE_NAME_ATTRIBUTE = 'data-page-page-name';

/**
 * Attribute carrying the device type, checked in preference order.
 *
 * `data-poly-devicetype` is stamped by PolyO (NEMO-5498) and is the durable source once FR-3
 * removes the LEGO header/footer. `data-devicetype` is the legacy attribute the LEGO
 * design-package page template (`LegoWidgetsConfig-Sell/Sell.template`) emits, correct only
 * while that markup is still on the page. Reading poly-first and falling back keeps the suffix
 * intact on both sides of the cutover.
 */
const POLY_DEVICE_TYPE_ATTRIBUTE = 'data-poly-devicetype';
const LEGACY_DEVICE_TYPE_ATTRIBUTE = 'data-devicetype';

/**
 * Device suffix appended to a CTA identifier, keyed by `data-devicetype` value.
 *
 * Verbatim from `adobe-analytics.es` L40-46, including all three mobile spellings the LEGO
 * page template emits.
 */
const DEVICE_SUFFIXES: Record<string, string> = {
  desktop: 'D',
  tablet: 'T',
  mobile: 'M',
  mobilePortrait: 'M',
  mobileLandscape: 'M',
};

/**
 * An authority or path naming a redirect wrapper counts as leading off site even when the
 * host matches the current page, because the wrapper's whole purpose is to forward off it.
 *
 * `redirect` has to be a whole path segment or hostname component — `/redirect/out` and
 * `redirect.example.com` qualify, `/guides/redirects-explained` does not. Matching it as a
 * bare substring would type ordinary editorial pages that merely mention redirects as
 * off-site exits, pooling them with the genuine ones in exit-link reporting.
 */
const REDIRECT_TARGET_PATTERN = /(^|[./])redirect([./?#]|$)/;

/** Longest derived slug kept, before the prefix is added. */
const MAX_DERIVED_SLUG_LENGTH = 40;

/** Where an identifier comes from for one tracked control. */
export interface CtaTrackingSource {
  /** Author-supplied identifier. Always wins over derivation. */
  ctaId?: string;
  /** Link target. Preferred derivation input — an href is stabler than DOM position or copy. */
  href?: string;
  /** Visible label, used to derive when there is no usable href. */
  label?: string;
  /**
   * Navigation target consulted only to classify `linkType`, never to derive the identifier.
   *
   * Derivation and classification usually share the same href, but not always: a
   * LinkCollection card derives its id from the card heading (NEMO-5504, because card hrefs
   * are frequently a shared section path) yet must still be typed as an exit link by where it
   * points. Setting `linkTarget` types the click without pulling the href into derivation.
   * Falls back to {@link href} when omitted, so a control whose href is both its derivation
   * source and its destination sets only `href`.
   */
  linkTarget?: string;
}

interface AdobeSiteCatalyst {
  linkTrackVars?: string;
  tl?: (linkObject: boolean | HTMLElement, linkType: string, linkName: string) => void;
  [variable: string]: unknown;
}

declare global {
  interface Window {
    /** Adobe SiteCatalyst s-code object, loaded by PolyO's inline analytics. */
    s?: AdobeSiteCatalyst;
    /**
     * Identifier Adobe Activity Map and ClickMap attribute a click to.
     *
     * Adobe reads this as a bare global off the window, not as a property of the s-code
     * object, so it is declared here rather than on {@link AdobeSiteCatalyst}.
     */
    s_objectID?: string;
  }
}

/** The one method this hook needs off the `window.PolyConsent` runtime. */
interface ConsentReader {
  hasConsent(category: 'performance'): boolean | null;
}

/**
 * Reads the consent runtime structurally rather than through a `Window` augmentation.
 *
 * `PolyConsent` ships in a separate bundle (`poly-consent-manager-v1.js`) that this one
 * must not depend on: the components bundle has to build and run on a page where the
 * consent manager never loaded.
 */
function getConsentReader(): ConsentReader | undefined {
  return (window as unknown as { PolyConsent?: ConsentReader }).PolyConsent;
}

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .slice(0, MAX_DERIVED_SLUG_LENGTH)
    .replace(/^_+|_+$/g, '');
}

/**
 * YouTube watch URLs carry the video identity in `?v=`, not the path, so every
 * `youtube.com/watch?v=…` would otherwise derive the same `watch` slug and pool distinct
 * videos under one identifier. Scoped to YouTube hosts deliberately: a generic `v` query
 * parameter is commonly a cache-buster, and folding it in elsewhere would change the derived
 * identifier of unrelated links between deploys.
 */
const YOUTUBE_HOST_PATTERN = /(^|\.)youtube(-nocookie)?\.com$/i;

/**
 * A `v` value kept in a derived identifier. YouTube video ids are case-sensitive and use both
 * `-` and `_`, so the id is kept verbatim rather than slugged — slugging would report
 * `AbC123xyz_1` and `abc123xyz_1`, or `a-b` and `a_b`, as one video. The character class keeps
 * anything else out of the identifier (notably `>`, the page-name separator in `prop28`), and
 * the length bound is the same one every derived slug gets; real ids are 11 characters.
 */
const YOUTUBE_VIDEO_ID_PATTERN = new RegExp(`^[A-Za-z0-9_-]{1,${MAX_DERIVED_SLUG_LENGTH}}$`);

function youtubeVideoId(url: URL, lastPathSegment: string): string {
  if (!YOUTUBE_HOST_PATTERN.test(url.hostname) || lastPathSegment !== 'watch') {
    return '';
  }
  const videoId = url.searchParams.get('v') ?? '';
  return YOUTUBE_VIDEO_ID_PATTERN.test(videoId) ? videoId : '';
}

function slugFromHref(href: string): string {
  try {
    const url = new URL(href, window.location.origin);
    const lastPathSegment = url.pathname.split('/').filter(Boolean).pop() ?? '';
    const fragment = url.hash.replace('#', '');
    const videoId = youtubeVideoId(url, lastPathSegment);
    if (!videoId) {
      return toSlug([lastPathSegment, fragment].filter(Boolean).join('_'));
    }
    return [toSlug(lastPathSegment), videoId, toSlug(fragment)].filter(Boolean).join('_');
  } catch {
    return '';
  }
}

/**
 * Classifies a link target as leading off site, so Adobe reports the click as an exit link.
 *
 * The decision is replicated natively rather than delegated to LeadCode. LEGO reaches it
 * through `window.leadCode.isUrlExternal` (`adobe-analytics.es` L59-70 calling
 * `leadcode.vendor.js` L793), which is unavailable here on two counts: LeadCode ships as a
 * separate bundle that this one must build and run without, and it exposes only the
 * `LeadCode` constructor — never the lowercase `leadCode` instance that expression reads, so
 * the check would be permanently false rather than merely absent. Report continuity needs the
 * classification to agree, not the code to be shared.
 *
 * Replicated decision: an `http`/`https` target whose host differs from the current page leads
 * off site; any other target does so only when its scheme, host or path names a redirect
 * wrapper. Relative paths, fragments, `mailto:` and `tel:` therefore stay on-site, matching
 * LEGO, whose regex only recognises absolute and protocol-relative URLs.
 *
 * An href `URL` rejects counts as on-site rather than throwing — this runs inside a click
 * handler mid-navigation.
 */
function isExternalLinkTarget(href: string | undefined): boolean {
  if (!href) {
    return false;
  }

  let target: URL;
  try {
    target = new URL(href, window.location.href);
  } catch {
    return false;
  }

  const isWebTarget = target.protocol === 'http:' || target.protocol === 'https:';
  if (isWebTarget && target.host !== window.location.host) {
    return true;
  }

  return REDIRECT_TARGET_PATTERN.test(`${target.origin}${target.pathname}`);
}

/**
 * Appends the device suffix LEGO reports CTA identifiers under.
 *
 * Replicates `adobe-analytics.es` L89-100 (`attachDeviceTypeToId`): the first element carrying
 * a device-type attribute supplies the value, which is mapped and joined onto the identifier
 * with an underscore, producing ids like `L1_a_start_D`. A value outside {@link DEVICE_SUFFIXES}
 * contributes an empty segment, leaving the trailing separator — kept deliberately, because the
 * point of this suffix is to be byte-identical to what LEGO emits for the same page.
 *
 * `data-poly-devicetype` (PolyO, NEMO-5498) is preferred over the legacy `data-devicetype` the
 * LEGO page template emits. FR-3 removes that LEGO-baked markup, at which point only the poly
 * attribute remains; before cutover the legacy fallback keeps the suffix correct. A page
 * carrying neither reports the bare identifier — the same fallback LEGO takes.
 */
function attachDeviceType(ctaId: string): string {
  const deviceElement =
    document.querySelector(`[${POLY_DEVICE_TYPE_ATTRIBUTE}]`) ??
    document.querySelector(`[${LEGACY_DEVICE_TYPE_ATTRIBUTE}]`);
  if (!deviceElement) {
    return ctaId;
  }

  // The selectors above only match an element carrying one of the two attributes, so one of these
  // reads is always non-null; the empty-string fallback only satisfies the index type.
  const deviceType =
    deviceElement.getAttribute(POLY_DEVICE_TYPE_ATTRIBUTE) ?? deviceElement.getAttribute(LEGACY_DEVICE_TYPE_ATTRIBUTE);

  return [ctaId, DEVICE_SUFFIXES[deviceType ?? '']].join('_');
}

/**
 * Resolves the identifier a click reports under.
 *
 * Derivation is deterministic — the same href or label always yields the same
 * identifier — so a report can be read across deploys without the ids shifting under it.
 * Returns undefined when there is nothing to derive from, and the click goes untracked
 * rather than being reported under a placeholder that would pool unrelated clicks.
 */
export function resolveCtaId(prefix: CtaPrefix, source: CtaTrackingSource): string | undefined {
  if (source.ctaId) {
    return source.ctaId;
  }

  const slug = (source.href && slugFromHref(source.href)) || (source.label ? toSlug(source.label) : '');

  return slug ? `${prefix}_${slug}` : undefined;
}

/**
 * Reports a CTA click to Adobe Analytics as `pageName>ctaId_device`, the value LEGO's
 * `adobe-analytics.es` produces — for example `US:SD:SOA-home>L1_a_start_D`.
 *
 * Parity with that handler means four things beyond the name format: the identifier goes in
 * `prop28`, `linkTrackVars` names `prop28` so Adobe actually serialises it on the beacon,
 * `window.s_objectID` carries the same value for Activity Map, and the link type distinguishes
 * an off-site exit from an on-site click. See `adobe-analytics.es` L72-74.
 *
 * Gated on performance consent, and skips on both a denial and an unresolved state:
 * `hasConsent` is tri-state, and `null` means the seller has not decided yet, so firing
 * on it would track someone mid-decision on precisely the domains that require a banner.
 * An `=== false` check would do exactly that.
 *
 * When `PolyConsent` is absent altogether the click is tracked. That is the A6 fail-open
 * posture for the performance category: a consent-manager that failed to load must not
 * silently blind analytics, and it is also how these pages behave today.
 *
 * A missing s-code object or page name is a silent no-op. Tracking is fire-and-forget and
 * runs during navigation, so it must never throw or delay the click.
 */
export function trackCtaClick(prefix: CtaPrefix, source: CtaTrackingSource): void {
  try {
    const consent = getConsentReader()?.hasConsent('performance');
    if (consent === false || consent === null) {
      return;
    }

    const resolvedId = resolveCtaId(prefix, source);
    if (!resolvedId) {
      return;
    }

    // Checked before the s-code object is touched, so a missing page name leaves s.tl,
    // prop28 and s_objectID alone. LEGO's adobe-analytics.es only binds its click handlers
    // when #omniture-details exists, and a `>ctaId` row with no page identity would pool
    // clicks into a malformed dimension, which is worse than a missing click.
    const pageName = document.getElementById(OMNITURE_ELEMENT_ID)?.getAttribute(OMNITURE_PAGE_NAME_ATTRIBUTE);
    if (!pageName) {
      return;
    }

    const siteCatalyst = window.s;
    if (typeof siteCatalyst?.tl !== 'function') {
      return;
    }

    const linkName = `${pageName}>${attachDeviceType(resolvedId)}`;
    const linkType = isExternalLinkTarget(source.linkTarget ?? source.href) ? LINK_TYPE_EXTERNAL : LINK_TYPE_CUSTOM;

    siteCatalyst.linkTrackVars = CTA_PROPERTY;
    siteCatalyst[CTA_PROPERTY] = linkName;
    window.s_objectID = linkName;
    siteCatalyst.tl(true, linkType, linkName);
  } catch {
    // Swallowed to keep reporting fire-and-forget. Every collaborator here is third-party
    // page state this bundle does not own — the consent runtime, the s-code object and its
    // variables — so any of them can throw. This runs synchronously inside a click handler,
    // ahead of the navigation or popup the click is actually for, and a throw escaping it
    // would take that behaviour down with it. A lost report is the lesser failure.
  }
}

/**
 * Returns a stable click handler that reports the CTA to Adobe Analytics.
 *
 * Use at the top level of a component. Inside a `map` over links, call
 * {@link trackCtaClick} directly — a hook cannot be called in a loop.
 */
export function useClickTracking(
  prefix: CtaPrefix,
  source: CtaTrackingSource,
): () => void {
  const { ctaId, href, label, linkTarget } = source;

  return useCallback(() => {
    trackCtaClick(prefix, { ctaId, href, label, linkTarget });
  }, [prefix, ctaId, href, label, linkTarget]);
}
